import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getPermissions, createRole, updateRole } from '../api';
import { parseApiError } from '@/common/utils';
import {
  Button,
  SearchInput,
  SideDrawer,
  SkeletonBox,
  SkeletonText,
} from '@/components';
import { usePermissions } from '@/common/hooks/usePermissions';
import { PERMISSIONS } from '@/common/config/permissions';
import { formatModuleName, groupPermissions } from '../utils/roleUtils';

const PERMISSION_HIERARCHY = ['read', 'add', 'edit', 'delete'];

const normalizePermissions = (initialIds, permissionsList) => {
  if (!initialIds || initialIds.length === 0) return [];
  const groups = groupPermissions(permissionsList);
  const idSet = new Set(initialIds);

  groups.forEach((group) => {
    let highestSelectedIndex = -1;
    for (let i = PERMISSION_HIERARCHY.length - 1; i >= 0; i--) {
      const perm = group.actions[PERMISSION_HIERARCHY[i]];
      if (perm && idSet.has(perm.id)) {
        highestSelectedIndex = i;
        break;
      }
    }
    if (highestSelectedIndex > -1) {
      for (let i = 0; i < highestSelectedIndex; i++) {
        const lowerPerm = group.actions[PERMISSION_HIERARCHY[i]];
        if (lowerPerm) idSet.add(lowerPerm.id);
      }
    }
  });

  // Dependency rule: CRM Leads (All) cannot be selected without at least one CRM Leads permission
  const crmLeadsGroup = groups.find(
    (g) => formatModuleName(g.name) === 'CRM Leads',
  );
  const crmLeadsAllGroup = groups.find(
    (g) => formatModuleName(g.name) === 'CRM Leads (All)',
  );
  const hasAnyCrmLeads =
    !!crmLeadsGroup &&
    Object.values(crmLeadsGroup.actions).some(
      (perm) => perm && idSet.has(perm.id),
    );
  if (!hasAnyCrmLeads && crmLeadsAllGroup) {
    Object.values(crmLeadsAllGroup.actions).forEach((perm) => {
      if (perm) idSet.delete(perm.id);
    });
  }

  return Array.from(idSet);
};

export function RoleModal({ isOpen, onClose, companyId, editingRole }) {
  const queryClient = useQueryClient();
  const [apiErrors, setApiErrors] = useState({});
  const [highlightLeadsOverview, setHighlightLeadsOverview] = useState(false);
  const [highlightProfile, setHighlightProfile] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permission_ids: [],
  });

  const { can } = usePermissions();

  const pulseCrmLeadsRequirement = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    // Force re-trigger animation even if highlight is already active
    setHighlightLeadsOverview(false);
    requestAnimationFrame(() => {
      setHighlightLeadsOverview(true);
      setTimeout(() => {
        const el = document.getElementById('role-row-CRM Leads');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    });
  };

  // Initialize form data when modal opens or editingRole changes
  useEffect(() => {
    if (isOpen) {
      setApiErrors({});
      setHighlightLeadsOverview(false);
      setHighlightProfile(false);
      if (editingRole) {
        setFormData({
          name: editingRole.name || '',
          description: editingRole.description || '',
          permission_ids: editingRole.permissions?.map((p) => p.id) || [],
        });
      } else {
        setFormData({
          name: '',
          description: '',
          permission_ids: [],
        });
      }
    }
  }, [isOpen, editingRole]);

  useEffect(() => {
    if (highlightLeadsOverview) {
      const timer = setTimeout(() => {
        setHighlightLeadsOverview(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightLeadsOverview]);

  useEffect(() => {
    if (highlightProfile) {
      const timer = setTimeout(() => {
        setHighlightProfile(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightProfile]);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: permissionsData, isLoading: isLoadingPermissions } = useQuery({
    queryKey: ['companyPermissions', companyId],
    queryFn: () => getPermissions(companyId, {}),
    enabled: !!companyId && isOpen,
  });

  let fullPermissionsList = [];
  if (Array.isArray(permissionsData)) {
    fullPermissionsList = permissionsData;
  } else if (permissionsData) {
    if (permissionsData.results && Array.isArray(permissionsData.results)) {
      fullPermissionsList = permissionsData.results;
    } else {
      fullPermissionsList = Object.values(permissionsData).flat();
    }
  }

  // We no longer pre-filter here because groupPermissions handles the filtering.
  // This keeps the structure intact.
  let permissionsList = permissionsData;

  useEffect(() => {
    if (
      isOpen &&
      fullPermissionsList.length > 0 &&
      formData.permission_ids.length > 0
    ) {
      const normalized = normalizePermissions(
        formData.permission_ids,
        fullPermissionsList,
      );
      if (normalized.length !== formData.permission_ids.length) {
        setFormData((prev) => ({ ...prev, permission_ids: normalized }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsList, isOpen]); // formData.permission_ids is intentionally excluded to prevent loops

  const handleGrantAll = () => {
    const allIds = fullPermissionsList.map((p) => p.id);
    setFormData((prev) => ({ ...prev, permission_ids: allIds }));
  };

  const handleClearAll = () => {
    setFormData((prev) => ({ ...prev, permission_ids: [] }));
  };

  const createMutation = useMutation({
    mutationFn: (payload) => {
      if (!can(PERMISSIONS.ROLES.CREATE))
        throw new Error(
          'Permission denied: You do not have access to create roles.',
        );
      return createRole({ companyId, payload });
    },
    onSuccess: () => {
      toast.success('Role created successfully!');
      onClose();
      queryClient.invalidateQueries(['companyRoles', companyId]);
    },
    onError: (err) => {
      if (err?.response?.status >= 500) {
        toast.error('Internal Server Error. Please try again later.');
        return;
      }
      const { message, fieldErrors } = parseApiError(err);
      setApiErrors(fieldErrors);
      let finalMessage = message;
      const keys = Object.keys(fieldErrors);
      if (keys.length > 0) {
        const firstKey = keys[0];
        const formattedKey =
          firstKey.charAt(0).toUpperCase() + firstKey.slice(1);
        finalMessage = `${formattedKey}: ${fieldErrors[firstKey]}`;
      }
      toast.error(finalMessage || 'Failed to create role');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => {
      if (!can(PERMISSIONS.ROLES.UPDATE))
        throw new Error(
          'Permission denied: You do not have access to update roles.',
        );
      return updateRole({ companyId, roleId: id, payload });
    },
    onSuccess: () => {
      toast.success('Role updated successfully!');
      onClose();
      queryClient.invalidateQueries(['companyRoles', companyId]);
    },
    onError: (err) => {
      if (err?.response?.status >= 500) {
        toast.error('Internal Server Error. Please try again later.');
        return;
      }
      const { message, fieldErrors } = parseApiError(err);
      setApiErrors(fieldErrors);
      let finalMessage = message;
      const keys = Object.keys(fieldErrors);
      if (keys.length > 0) {
        const firstKey = keys[0];
        const formattedKey =
          firstKey.charAt(0).toUpperCase() + firstKey.slice(1);
        finalMessage = `${formattedKey}: ${fieldErrors[firstKey]}`;
      }
      toast.error(finalMessage || 'Failed to update role');
    },
  });

  const handleInputChange = (e) => {
    let { name, value } = e.target;

    // Capitalize only the first letter of the string for the name field
    if (name === 'name' && value.length > 0) {
      value = value.charAt(0).toUpperCase() + value.slice(1);
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (apiErrors[name]) {
      setApiErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handlePermissionToggle = (group, actionKey) => {
    const formattedName = formatModuleName(group.name);
    const currentIds = new Set(formData.permission_ids || []);
    const permId = group.actions[actionKey].id;
    const isSelected = currentIds.has(permId);

    const allGroups = groupPermissions(fullPermissionsList);
    const crmLeadsGroup = allGroups.find(
      (g) => formatModuleName(g.name) === 'CRM Leads',
    );
    const crmLeadsManagementGroup = allGroups.find(
      (g) => formatModuleName(g.name) === 'CRM Leads Management',
    );
    const inquiriesGroup = allGroups.find(
      (g) => formatModuleName(g.name) === 'Inquiries',
    );
    const profileGroup = allGroups.find(
      (g) => formatModuleName(g.name) === 'Profile',
    );
    const profileReadId = profileGroup?.actions?.read?.id;
    const digitalCardGroup = allGroups.find(
      (g) => formatModuleName(g.name) === 'Digital Card',
    );

    const hasAnyCrmLeadsPermission = (selectedIds) =>
      !!crmLeadsGroup &&
      Object.values(crmLeadsGroup.actions).some(
        (perm) => perm && selectedIds.has(perm.id),
      );

    if (!isSelected) {
      // Turning ON
      if (
        formattedName === 'CRM Leads (All)' &&
        !hasAnyCrmLeadsPermission(currentIds)
      ) {
        toast.error(
          'At least one CRM Leads permission is required to grant CRM Leads (All).',
        );
        pulseCrmLeadsRequirement();
        return;
      }

      if (
        formattedName === 'CRM Leads Management' &&
        !hasAnyCrmLeadsPermission(currentIds)
      ) {
        toast.error(
          'At least one CRM Leads permission is required to grant CRM Leads Management.',
        );
        pulseCrmLeadsRequirement();
        return;
      }

      if (
        formattedName === 'Inquiries' &&
        !hasAnyCrmLeadsPermission(currentIds)
      ) {
        toast.error(
          'At least one CRM Leads permission is required to access Inquiries.',
        );
        pulseCrmLeadsRequirement();
        return;
      }

      if (formattedName === 'CRM Leads') {
        setHighlightLeadsOverview(false);
      }
      if (formattedName === 'Profile' && actionKey === 'read') {
        setHighlightProfile(false);
      }

      if (
        formattedName === 'Digital Card' &&
        profileReadId &&
        !currentIds.has(profileReadId)
      ) {
        toast.error(
          'Profile Read permission is required to access Digital Card.',
        );
        setSearchQuery('');
        setDebouncedSearch('');
        setHighlightProfile(true);
        setTimeout(() => {
          const el = document.getElementById('role-row-Profile');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 150);
        return;
      }
    }

    setFormData((prev) => {
      const nextIds = new Set(prev.permission_ids || []);
      const targetIndex = PERMISSION_HIERARCHY.indexOf(actionKey);

      if (!isSelected) {
        // Turning ON: select this and all lower available permissions
        for (let i = 0; i <= targetIndex; i++) {
          const lowerPerm = group.actions[PERMISSION_HIERARCHY[i]];
          if (lowerPerm) nextIds.add(lowerPerm.id);
        }
      } else {
        // Turning OFF: unselect this and all higher available permissions
        for (let i = targetIndex; i < PERMISSION_HIERARCHY.length; i++) {
          const higherPerm = group.actions[PERMISSION_HIERARCHY[i]];
          if (higherPerm) nextIds.delete(higherPerm.id);
        }

        // If no CRM Leads permissions remain, clear dependent modules
        if (
          formattedName === 'CRM Leads' &&
          !hasAnyCrmLeadsPermission(nextIds)
        ) {
          const crmLeadsAllGroup = allGroups.find(
            (g) => formatModuleName(g.name) === 'CRM Leads (All)',
          );
          if (crmLeadsAllGroup) {
            Object.values(crmLeadsAllGroup.actions).forEach((perm) => {
              if (perm) nextIds.delete(perm.id);
            });
          }
          if (crmLeadsManagementGroup) {
            Object.values(crmLeadsManagementGroup.actions).forEach((perm) => {
              if (perm) nextIds.delete(perm.id);
            });
          }
          if (inquiriesGroup) {
            Object.values(inquiriesGroup.actions).forEach((perm) => {
              if (perm) nextIds.delete(perm.id);
            });
          }
        }

        if (formattedName === 'Profile' && actionKey === 'read') {
          if (digitalCardGroup) {
            Object.values(digitalCardGroup.actions).forEach((perm) => {
              if (perm) nextIds.delete(perm.id);
            });
          }
        }
      }

      return { ...prev, permission_ids: Array.from(nextIds) };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Extra validation on submit
    const allGroups = groupPermissions(fullPermissionsList);
    const crmLeadsGroup = allGroups.find(
      (g) => formatModuleName(g.name) === 'CRM Leads',
    );
    const crmLeadsAllGroup = allGroups.find(
      (g) => formatModuleName(g.name) === 'CRM Leads (All)',
    );
    const crmLeadsManagementGroup = allGroups.find(
      (g) => formatModuleName(g.name) === 'CRM Leads Management',
    );
    const inquiriesGroup = allGroups.find(
      (g) => formatModuleName(g.name) === 'Inquiries',
    );
    const profileGroup = allGroups.find(
      (g) => formatModuleName(g.name) === 'Profile',
    );
    const profileReadId = profileGroup?.actions?.read?.id;
    const digitalCardGroup = allGroups.find(
      (g) => formatModuleName(g.name) === 'Digital Card',
    );

    const selectedIds = new Set(formData.permission_ids || []);
    const hasAnyCrmLeads =
      !!crmLeadsGroup &&
      Object.values(crmLeadsGroup.actions).some(
        (perm) => perm && selectedIds.has(perm.id),
      );

    let hasLeadAll = false;
    if (crmLeadsAllGroup) {
      hasLeadAll = Object.values(crmLeadsAllGroup.actions).some(
        (perm) => perm && selectedIds.has(perm.id),
      );
    }

    let hasLeadManagement = false;
    if (crmLeadsManagementGroup) {
      hasLeadManagement = Object.values(crmLeadsManagementGroup.actions).some(
        (perm) => perm && selectedIds.has(perm.id),
      );
    }

    let hasInquiries = false;
    if (inquiriesGroup) {
      hasInquiries = Object.values(inquiriesGroup.actions).some(
        (perm) => perm && selectedIds.has(perm.id),
      );
    }

    let hasDigitalCard = false;
    if (digitalCardGroup) {
      hasDigitalCard = Object.values(digitalCardGroup.actions).some(
        (perm) => perm && selectedIds.has(perm.id),
      );
    }

    if (hasLeadAll && !hasAnyCrmLeads) {
      toast.error(
        'At least one CRM Leads permission is required to grant CRM Leads (All).',
      );
      pulseCrmLeadsRequirement();
      return;
    }

    if (hasLeadManagement && !hasAnyCrmLeads) {
      toast.error(
        'At least one CRM Leads permission is required to grant CRM Leads Management.',
      );
      pulseCrmLeadsRequirement();
      return;
    }

    if (hasInquiries && !hasAnyCrmLeads) {
      toast.error(
        'At least one CRM Leads permission is required to access Inquiries.',
      );
      pulseCrmLeadsRequirement();
      return;
    }

    if (
      hasDigitalCard &&
      profileReadId &&
      !formData.permission_ids.includes(profileReadId)
    ) {
      toast.error(
        'Profile Read permission is required to access Digital Card.',
      );
      return;
    }

    if (editingRole) {
      updateMutation.mutate({ id: editingRole.id, payload: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={() => {
        if (!createMutation.isPending && !updateMutation.isPending) {
          onClose();
        }
      }}
      width="xl"
      className="role-form-drawer"
      showClose={false}
      bodyClassName="p-0 flex flex-col min-h-0 !overflow-hidden"
      headerClassName="!px-6 !py-5"
      header={
        <div className="flex items-center justify-between w-full gap-3">
          <h2 className="text-[20px] leading-[28px] font-semibold text-[#1F2241] font-sf-pro-display">
            {editingRole ? 'Edit Role' : 'Add New Role'}
          </h2>
          <button
            type="button"
            onClick={() =>
              !createMutation.isPending &&
              !updateMutation.isPending &&
              onClose()
            }
            className="w-8 h-8 rounded-[8px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors focus:outline-none shrink-0"
            disabled={createMutation.isPending || updateMutation.isPending}
            aria-label="Close"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.75"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      }
    >
      <form
        id="add-role-form"
        onSubmit={handleSubmit}
        className="flex flex-col h-full overflow-hidden"
        autoComplete="off"
      >
        <div className="flex-1 overflow-y-auto custom-scrollbar p-6 flex flex-col gap-5 bg-white">
          {/* Basic Information */}
          <div className="flex flex-col gap-3">
            <h4 className="text-[18px] leading-[26px] font-medium text-[#50AB3D] font-sf-pro-display">
              Basic Information
            </h4>
            <div className="bg-[#F5F5F5] rounded-[12px] p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="name"
                    className="text-[14px] font-medium text-text-secondary"
                  >
                    Role Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g. Manager"
                    disabled={
                      createMutation.isPending || updateMutation.isPending
                    }
                    className={`w-full h-11 px-4 rounded-xl bg-white border text-[#1F2241] text-[14px] font-medium placeholder:text-[#868686] focus:outline-none transition-colors ${apiErrors.name ? 'border-red-500' : 'border-border-default focus:border-primary'}`}
                  />
                  {apiErrors.name && (
                    <span className="text-red-500 text-xs font-medium">
                      {apiErrors.name}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="description"
                    className="text-[14px] font-medium text-text-secondary"
                  >
                    Description
                  </label>
                  <input
                    type="text"
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Brief description of the role's purpose"
                    disabled={
                      createMutation.isPending || updateMutation.isPending
                    }
                    className={`w-full h-11 px-4 rounded-xl bg-white border text-[#1F2241] text-[14px] font-medium placeholder:text-[#868686] focus:outline-none transition-colors ${apiErrors.description ? 'border-red-500' : 'border-border-default focus:border-primary'}`}
                  />
                  {apiErrors.description && (
                    <span className="text-red-500 text-xs font-medium">
                      {apiErrors.description}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Permissions Matrix */}
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-4">
              <h4 className="text-[18px] leading-[26px] font-medium text-[#50AB3D] font-sf-pro-display whitespace-nowrap">
                Permissions Matrix
              </h4>
              <div className="flex gap-2 items-center">
                <button
                  type="button"
                  onClick={handleGrantAll}
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-surface-mint text-primary hover:bg-layer-7-hover transition-colors border border-primary/20 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                >
                  Grant Full Access
                </button>
                <button
                  type="button"
                  onClick={handleClearAll}
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white text-text-muted hover:text-text-primary transition-colors border border-border-default hover:border-text-muted disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                >
                  Clear All
                </button>
              </div>
            </div>

            <div className="bg-[#F5F5F5] rounded-[12px] p-4 flex flex-col gap-3">
              <SearchInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search permissions..."
                inputClassName="h-10 !bg-[#FFFFFF]"
                disabled={createMutation.isPending || updateMutation.isPending}
              />

              <div className="bg-white border border-border-default rounded-xl overflow-hidden flex flex-col min-h-0">
                {(() => {
                  if (isLoadingPermissions) {
                    return (
                      <div className="overflow-auto custom-scrollbar max-h-[400px]">
                        <table className="w-full text-left border-collapse min-w-[500px]">
                          <thead className="sticky top-0 bg-white z-10 border-b border-border-default shadow-sm">
                            <tr>
                              <th className="py-3 px-4 text-xs font-bold text-text-secondary uppercase tracking-wider w-[40%]">
                                Menu / Modules
                              </th>
                              <th className="py-3 px-4 text-xs font-bold text-text-secondary uppercase tracking-wider text-center w-[15%]">
                                Read
                              </th>
                              <th className="py-3 px-4 text-xs font-bold text-text-secondary uppercase tracking-wider text-center w-[15%]">
                                Add
                              </th>
                              <th className="py-3 px-4 text-xs font-bold text-text-secondary uppercase tracking-wider text-center w-[15%]">
                                Edit
                              </th>
                              <th className="py-3 px-4 text-xs font-bold text-text-secondary uppercase tracking-wider text-center w-[15%]">
                                Delete
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border-default">
                            {[...Array(6)].map((_, i) => (
                              <tr key={`skeleton-perm-${i}`}>
                                <td className="py-3 px-4">
                                  <SkeletonText width="w-32" height="h-4" />
                                </td>
                                {['read', 'add', 'edit', 'delete'].map(
                                  (actionKey) => (
                                    <td
                                      key={actionKey}
                                      className="py-3 px-4 text-center align-middle"
                                    >
                                      <SkeletonBox className="w-5 h-5 mx-auto" />
                                    </td>
                                  ),
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  }

                  const groupedPerms = groupPermissions(
                    permissionsList,
                    debouncedSearch,
                  );

                  if (groupedPerms.length === 0) {
                    return (
                      <div className="p-8 text-center text-sm text-text-muted">
                        No permissions found.
                      </div>
                    );
                  }

                  return (
                    <div className="overflow-auto custom-scrollbar max-h-[400px]">
                      <table className="w-full text-left border-collapse min-w-[500px]">
                        <thead className="sticky top-0 bg-white z-10 border-b border-border-default shadow-sm">
                          <tr>
                            <th className="py-3 px-4 text-xs font-bold text-text-secondary uppercase tracking-wider w-[40%]">
                              Menu / Modules
                            </th>
                            <th className="py-3 px-4 text-xs font-bold text-text-secondary uppercase tracking-wider text-center w-[15%]">
                              Read
                            </th>
                            <th className="py-3 px-4 text-xs font-bold text-text-secondary uppercase tracking-wider text-center w-[15%]">
                              Add
                            </th>
                            <th className="py-3 px-4 text-xs font-bold text-text-secondary uppercase tracking-wider text-center w-[15%]">
                              Edit
                            </th>
                            <th className="py-3 px-4 text-xs font-bold text-text-secondary uppercase tracking-wider text-center w-[15%]">
                              Delete
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border-default">
                          {groupedPerms.map((group, idx) => {
                            const groupName = formatModuleName(group.name);
                            const isRowHighlighted =
                              (highlightLeadsOverview &&
                                groupName === 'CRM Leads') ||
                              (highlightProfile && groupName === 'Profile');

                            return (
                              <tr
                                key={idx}
                                id={`role-row-${groupName}`}
                                className={`transition-all duration-300 ${
                                  isRowHighlighted
                                    ? 'bg-[#EBF4EA] ring-2 ring-primary animate-pulse z-10 relative shadow-[0_0_15px_rgba(80,171,61,0.35)]'
                                    : 'hover:bg-layer-7-hover/30'
                                }`}
                              >
                                <td className="py-3 px-4">
                                  <div className="flex flex-col gap-0.5">
                                    <span
                                      className={`text-sm font-semibold transition-colors ${isRowHighlighted ? 'text-primary' : 'text-text-primary'}`}
                                    >
                                      {groupName}
                                    </span>
                                    {isRowHighlighted &&
                                      groupName === 'CRM Leads' && (
                                        <span className="text-[11px] font-medium text-primary">
                                          Select at least one permission here
                                          first
                                        </span>
                                      )}
                                  </div>
                                </td>
                                {['read', 'add', 'edit', 'delete'].map(
                                  (actionKey) => {
                                    const perm = group.actions[actionKey];
                                    const isCheckboxHighlighted =
                                      isRowHighlighted &&
                                      (groupName === 'CRM Leads'
                                        ? Boolean(perm)
                                        : actionKey === 'read');
                                    return (
                                      <td
                                        key={actionKey}
                                        className="py-3 px-4 text-center align-middle"
                                      >
                                        {perm ? (
                                          <div className="flex justify-center">
                                            <div className="relative flex items-center group/check">
                                              <input
                                                type="checkbox"
                                                checked={formData.permission_ids.includes(
                                                  perm.id,
                                                )}
                                                onChange={() =>
                                                  handlePermissionToggle(
                                                    group,
                                                    actionKey,
                                                  )
                                                }
                                                disabled={
                                                  createMutation.isPending ||
                                                  updateMutation.isPending
                                                }
                                                className={`w-5 h-5 appearance-none border rounded transition-all peer cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 ${
                                                  isCheckboxHighlighted
                                                    ? 'border-primary bg-primary/20 ring-4 ring-primary/40 scale-110'
                                                    : 'border-border-default bg-white checked:bg-primary checked:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50'
                                                }`}
                                              />
                                              <svg
                                                className="absolute inset-0 w-5 h-5 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="3"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              >
                                                <polyline points="20 6 9 17 4 12" />
                                              </svg>
                                            </div>
                                          </div>
                                        ) : (
                                          <span className="text-text-muted/30">
                                            -
                                          </span>
                                        )}
                                      </td>
                                    );
                                  },
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
              {apiErrors.permission_ids && (
                <span className="text-red-500 text-xs font-medium">
                  {apiErrors.permission_ids}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-5 border-t border-[#EBEBEB] shrink-0 gap-3 bg-white">
          <Button
            type="submit"
            variant="primary"
            disabled={createMutation.isPending || updateMutation.isPending}
            className="h-[44px] px-5 text-[15px] font-medium rounded-[8px] gap-2 min-w-[140px] flex items-center justify-center"
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <svg
                className="animate-spin h-4 w-4 text-white"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : editingRole ? (
              'Update Role'
            ) : (
              'Save Role'
            )}
          </Button>
        </div>
      </form>
    </SideDrawer>
  );
}
